import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'

import { theme } from '@factorialco/deprecated-design-system'

import { trackingEvents } from 'modules/core/lib/Tracking/events'
import { track } from 'modules/core/lib/Tracking/tracker'

interface NavatticProps {
  demoUrl: string
  title?: string
  moduleSlug: string
  isOpen: boolean
}

export const Navattic = ({ demoUrl, title = 'Demo', moduleSlug, isOpen }: NavatticProps) => {
  const [isModalOpen, setIsModalOpen] = useState(isOpen)
  const [isVisible, setIsVisible] = useState(isOpen)
  const { pathname } = useLocation()

  const openDemo = () => {
    track(trackingEvents.marketplaceModules.openNavatticDemoModal, {
      module: moduleSlug,
      origin: pathname,
      source: 'marketplace_side_panel',
    })
    setIsModalOpen(true)
    setTimeout(() => setIsVisible(true), 10)
  }

  const closeDemo = () => {
    setIsModalOpen(false)
    setIsVisible(false)
  }

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isModalOpen) {
        closeDemo()
      }
    }

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isModalOpen])

  const demoId = demoUrl.split('/').pop() || ''

  const overlayStyles = {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: `rgba(0, 0, 0, ${isVisible ? 0.8 : 0})`,
    zIndex: theme.zIndex.modals,
    display: isModalOpen ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.3s ease',
  }

  const modalContentStyles = {
    position: 'relative' as const,
    width: 'calc(100vw - 40px)',
    height: 'calc(100vh - 40px)',
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    transform: isVisible ? 'scale(1)' : 'scale(0.95)',
    opacity: isVisible ? 1 : 0,
    transition: 'transform 0.3s ease, opacity 0.3s ease',
  }

  const closeButtonStyles = {
    position: 'absolute' as const,
    top: '20px',
    right: '20px',
    background: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    cursor: 'pointer',
    fontSize: '20px',
    zIndex: theme.zIndex.modals,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease, transform 0.2s ease',
  }

  const iframeStyles = {
    border: 'none',
    width: '100%',
    height: '100%',
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeDemo()
    }
  }

  const modalContent = (
    <div style={overlayStyles} onClick={handleOverlayClick}>
      <div style={modalContentStyles}>
        <button
          style={closeButtonStyles}
          onClick={closeDemo}
          aria-label="Close demo"
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)'
            e.currentTarget.style.transform = 'scale(1.1)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)'
            e.currentTarget.style.transform = 'scale(1)'
          }}
        >
          ×
        </button>
        <iframe
          src={demoUrl}
          style={iframeStyles}
          data-navattic-demo-id={demoId}
          allow="fullscreen"
          title={title}
        />
      </div>
    </div>
  )

  return {
    openDemo,
    modal: createPortal(modalContent, document.body),
  }
}
